// react
import { useTranslation } from 'react-i18next';

// types
import { Project, StatsEngagement } from '../../../../@types';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { ChipParticipation } from '../../../ui/ChipParticipation/ChipParticipation';
import { FlagText } from '../../../ui/FlagText/FlagText';
import { IconCircle } from '../../../ui/IconCircle/IconCircle';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// links
import { LinkProject } from '../../../links/LinkProject/LinkProject';

// utils
import { getDaysBetween, toRelativeTime } from '../../../../utils/dates';

// props
export interface TableBasicProjectsEngagementProps extends Omit<TableProps, 'headers'> {
	data: StatsEngagement[];
}

export const TableBasicProjectsEngagement = ({
	data = [],
	...props
}: TableBasicProjectsEngagementProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const headers: TableHeader[] = [
		{
			label: t('project'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				return (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>
							<IconCircle
								size="xs"
								TooltipProps={{
									componentsProps: { tooltip: { sx: { maxWidth: 160 } } },
									title: row.project.name,
								}}
								type="project"
							/>
						</Grid>
						<Grid flex={1} item>
							<LinkProject children={row.project.idWbg} project={row.project as Project} />
						</Grid>
					</Grid>
				);
			},
			value: 'name',
		},
		{
			label: t('country'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				return <FlagText country={row.project.country} />;
			},
			value: 'country',
		},
		{
			isSortable: true,
			label: t('avgTxPerMonth'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				return <Typography>{Math.round(row.transactions.avgPerMonth)}</Typography>;
			},
			value: 'avgPerMonth',
		},
		{
			isSortable: true,
			label: t('lastTransaction'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				let timeSince: string | undefined = '-';
				if (row.transactions.dateOfLast) {
					timeSince = toRelativeTime(new Date(row.transactions.dateOfLast));
				}
				return <Typography>{timeSince}</Typography>;
			},
			value: 'dateOfLast',
		},
		{
			isSortable: true,
			label: t('timeToFirstTx'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				let timeToFirst: string | undefined = '-';
				if (row.project.created && row.transactions.dateOfFirst) {
					const days = getDaysBetween({
						from: row.project.created,
						to: row.transactions.dateOfFirst,
					});
					timeToFirst = days !== null ? `${days} ${t('days')}` : '-';
				}
				return <Typography>{timeToFirst}</Typography>;
			},
			value: 'dateOfFirst',
		},
		{
			isSortable: true,
			label: t('entityParticipation'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				return <ChipParticipation entities={row.entities} />;
			},
			value: 'entitiesParticipation',
		},
		{
			isSortable: true,
			label: t('userParticipation'),
			render: (_: unknown, __: TableHeader, row: StatsEngagement) => {
				return <ChipParticipation users={row.users} />;
			},
			value: 'usersParticipation',
		},
	];

	return <Table color="grey" {...props} data={data} headers={headers} />;
};
